<template>
  <div class="report-container">
    <div class="subheader">
      <main-button flat @click="goBack">
        <v-icon class="arrow-back">arrow_back</v-icon>
        {{ adminText('organizationAll.back_61bb8d29', 'back') }}
      </main-button>
      <div v-if="!$apollo.loading && !isMobileVertical" class="right-controls">
        <div class="customize-button" @click="showCustomizeModal = !showCustomizeModal">
          <span class="customize-text">{{ adminText('organizationAll.customize_report_9158667d', 'Customize report') }}</span>
        </div>
        <div class="export-button" @click="showReportsExportModal = !showReportsExportModal">
          <span class="export-text">{{ adminText('organizationAll.export_51713409', 'export') }}</span>
          <img
            src="/assets/download.svg"
            :alt="adminText('organizationAll.download_icon_421b8552', 'Download icon')"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>
      </div>
    </div>
    <div v-if="!isMobileVertical" class="table-header">
      <h1 class="subs-title">{{ adminText('organizationAll.live_report_2dca02e3', 'Live Report') }}</h1>
      <v-text-field
        :label="adminText('organizationAll.search_events_f3e47cc3', 'Search Events')"
        append-icon="search"
        box
        style="min-width: 18rem"
        @change="handleSearchChange($event)"
      />
      <div class="filter-dates-container">
        <date-field v-model="dateStart" style="width: 100%" :label="adminText('organizationAll.start_date_9d7ab1a5', 'Start Date')" :max="dateEnd" />
        <date-field v-model="dateEnd" style="width: 100%" :label="adminText('organizationAll.end_date_84b14781', 'End Date')" :min="dateStart" />
      </div>
    </div>
    <div v-if="$apollo.loading">
      <spinner />
    </div>
    <div class="tables-container" :style="isMobileVertical && 'display: none'">
      <div class="table-container">
        <table id="data-table">
          <thead>
            <tr class="header-row">
              <th class="fixed-td fixed-hd left-top-td">{{ adminText('organizationAll.event_name_8e10d77c', 'Event name') }}</th>
              <th class="fixed-td fixed-hd left-top-td-second">{{ adminText('organizationAll.date_and_time_07c9c3a5', 'Date and time') }}</th>
              <th class="fixed-hd">{{ adminText('organizationAll.location_d219c681', 'Location') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.capacity_45bd908d', 'Capacity') }}</th>
              <th class="fixed-hd">{{ adminText('organizationAll.total_tickets_2ac4c693', 'Total Tickets') }}</th>
              <th class="fixed-hd">{{ adminText('organizationAll.available_7c62a142', 'Available') }}</th>
              <th class="fixed-hd">{{ adminText('organizationAll.reserved_67a6ff10', 'Reserved') }}</th>
              <th class="fixed-hd">{{ adminText('organizationAll.sold_e6560d9c', 'Sold') }}</th>
              <th class="fixed-hd">{{ adminText('organizationAll.gratis_00f5fed3', 'Gratis') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.promo_code_edfcc5e3', 'Promo code') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.average_price_205bddeb', 'Average price') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.number_of_transactions_b6109e40', 'Number of transactions') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.total_occupancy_e242d561', 'Total Occupancy') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.revenue_35cf82f9', 'Revenue') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="event in performancesList" :key="event.id">
              <td class="fixed-td">
                <a
                  :href="`/theater-organizations/${organizationSlug}/event/${event.id}/report`"
                  target="_blank"
                >{{ event.title }}</a>
              </td>
              <td class="fixed-td fixed-td-second">{{ formatDate(event.from) }}</td>
              <td>{{ getLocation(event) }}</td>
              <td class="tr-right-border">{{ getCapacity(event) }}</td>
              <td>{{ event.audienceCapacity }}</td>
              <td>
                {{
                  event.audienceCapacity -
                    event.report.totalSoldCount -
                    countPurchases(event.report.reservations) -
                    countPurchases(event.report.gratisOrders)
                }}
              </td>
              <td>
                {{ countPurchases(event.report.reservations) }} <br>
                <span style="color: #979797">
                  {{
                    (
                      (countPurchases(event.report.reservations) /
                      (event.audienceCapacity === 0 ? 1 : event.audienceCapacity)) *
                      100
                    ).toFixed(2)
                  }}
                  %
                </span>
              </td>
              <td>
                {{ event.report.totalSoldCount }} <br>
                <span style="color: #979797">
                  {{
                    (
                      (Number(event.report.totalSoldCount) /
                      (event.audienceCapacity === 0 ? 1 : event.audienceCapacity)) *
                      100
                    ).toFixed(2)
                  }}
                  %
                </span>
              </td>
              <td>
                {{ countPurchases(event.report.gratisOrders) }} <br>
                <span style="color: #979797">
                  {{
                    (
                      (countPurchases(event.report.gratisOrders) /
                      (event.audienceCapacity === 0 ? 1 : event.audienceCapacity)) *
                      100
                    ).toFixed(2)
                  }}
                  %
                </span>
              </td>
              <td class="tr-right-border">
                {{ event.report.totalWithPromoCode }} <br>
                <span style="color: #979797">
                  {{
                    (
                      (event.report.totalWithCoupon /
                      (event.audienceCapacity === 0 ? 1 : event.audienceCapacity)) *
                      100
                    ).toFixed(2)
                  }}
                  %
                </span>
              </td>
              <td class="tr-right-border">{{ event.report.averagePrice }} €</td>
              <td class="tr-right-border">{{ event.report.totalOrderCount }}</td>
              <td class="tr-right-border">{{ event.report.totalOccupancy }}</td>
              <td class="tr-right-border">
                {{ event.report.totalSoldOnlineAmount + event.report.totalSoldOfflineAmount }} €
              </td>
            </tr>
          </tbody>
          <tfoot />
        </table>
      </div>
      <div
        v-if="recapitulationData && recapitulationData.eventAmount > 0"
        class="table-container-recapitulation"
      >
        <table>
          <thead>
            <tr>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.event_amount_89bf5794', 'Event amount') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.capacity_45bd908d', 'Capacity') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.total_tickets_2ac4c693', 'Total Tickets') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.available_7c62a142', 'Available') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.reserved_67a6ff10', 'Reserved') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.sold_e6560d9c', 'Sold') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.gratis_00f5fed3', 'Gratis') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.promo_code_edfcc5e3', 'Promo code') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.average_price_205bddeb', 'Average price') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.number_of_transactions_b6109e40', 'Number of transactions') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.total_occupancy_20017422', 'Total occupancy') }}</th>
              <th class="fixed-hd tr-right-border">{{ adminText('organizationAll.total_revenue_1a21e656', 'Total revenue') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="tr-right-border">{{ recapitulationData.eventAmount }}</td>
              <td class="tr-right-border">{{ recapitulationData.capacity }}</td>
              <td class="tr-right-border">{{ recapitulationData.totalTickets }}</td>
              <td class="tr-right-border">{{ recapitulationData.available }}</td>
              <td class="tr-right-border">
                {{ recapitulationData.reserved }} <br>
                <span
                  style="color: #979797"
                >{{
                  (
                    (recapitulationData.reserved / recapitulationData.totalTickets || 0) * 100
                  ).toFixed(2)
                }}
                  %</span>
              </td>
              <td class="tr-right-border">
                {{ recapitulationData.sold }} <br>
                <span
                  style="color: #979797"
                >{{
                  (
                    (recapitulationData.sold / recapitulationData.totalTickets || 0) * 100
                  ).toFixed(2)
                }}
                  %</span>
              </td>
              <td class="tr-right-border">
                {{ recapitulationData.gratis }} <br>
                <span
                  style="color: #979797"
                >{{
                  (
                    (recapitulationData.gratis / recapitulationData.totalTickets || 0) * 100
                  ).toFixed(2)
                }}
                  %</span>
              </td>
              <td class="tr-right-border">
                {{ recapitulationData.promoCode }} <br>
                <span
                  style="color: #979797"
                >{{
                  (
                    (recapitulationData.promoCode / recapitulationData.totalTickets || 0) * 100
                  ).toFixed(2)
                }}
                  %</span>
              </td>
              <td class="tr-right-border">
                {{
                  recapitulationData.sumAveragePrice === 0
                    ? '/'
                    : (
                      recapitulationData.sumAveragePrice /
                      (recapitulationData.eventAmount === 0 ? 1 : recapitulationData.eventAmount)
                    ).toFixed(2)
                }}
                €
              </td>
              <td class="tr-right-border">{{ recapitulationData.numberOfTransactions }}</td>
              <td class="tr-right-border">
                {{
                  recapitulationData.sumTotalOccupancy === 0
                    ? '/'
                    : (
                      recapitulationData.sumTotalOccupancy /
                      (recapitulationData.eventAmount === 0 ? 1 : recapitulationData.eventAmount)
                    ).toFixed(2)
                }}
                %
              </td>
              <td class="tr-right-border">{{ recapitulationData.totalRevenue.toFixed(2) }} €</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-if="!$apollo.loading && isMobileVertical">
      <img
        src="/assets/phone-rotate-landscape.svg"
        :alt="adminText('organizationAll.phone_rotate_to_landscape_icon_e4c94cd6', 'Phone rotate to landscape icon')"
        class="rotate-phone-img"
      >
      <span class="rotate-phone-message">{{ adminText('organizationAll.rotate_your_phone_to_view_the_table_048dfcef', 'Rotate your phone to view the table') }}</span>
      <div class="mobile-export-container">
        <span
          class="mobile-export-description"
        >{{ adminText('organizationAll.you_can_also_export_and_open_the_data_table_i_89037cd3', 'You can also export and open the data table in a spreadsheet application.') }}</span>
        <div class="mobile-export-button" @click="showReportsExportModal = !showReportsExportModal">
          <span class="mobile-export-text">{{ adminText('organizationAll.export_51713409', 'export') }}</span>
          <img
            src="/assets/download.svg"
            :alt="adminText('organizationAll.download_icon_421b8552', 'Download icon')"
            style="width: 1.5rem; height: 1.5rem"
          >
        </div>
        <div class="mobile-export-button" @click="showCustomizeModal = !showCustomizeModal">
          <span class="mobile-export-text">{{ adminText('organizationAll.customize_report_9158667d', 'Customize report') }}</span>
        </div>
      </div>
    </div>
    <reports-export-modal
      v-if="showReportsExportModal"
      @handleExcelExport="handleExcelExport"
      @handleCsvExport="handleCsvExport"
      @close="showReportsExportModal = !showReportsExportModal"
    />
    <reports-customize-modal
      v-if="showCustomizeModal"
      :initial-from="dateStart"
      :initial-to="dateEnd"
      :organization-slug="organizationSlug"
      @generate="handleGenerateReport"
      @close="showCustomizeModal = !showCustomizeModal"
    />
  </div>
</template>

<script>
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import { GET_THEATER_SUMMARY_REPORTS } from '@/graphql/event.graphql'
import { OrganizationType, SeatState } from '@/types'
import moment from 'moment'
import { round } from 'mathjs'
import ReportsExportModal from '@/components/organizations/reports/ReportsExportModal.vue'
import ReportsCustomizeModal from '@/components/organizations/reports/ReportsCustomizeModal.vue'
import * as XLSX from 'xlsx'
import { ungroupedRouteNames } from '@/routing/routeNames/ungroupedRouteNames'

export default {
  mixins: [OrganizationLocalization],
  name: 'reports-container',
  components: { ReportsExportModal, ReportsCustomizeModal },
  props: {
    organizationSlug: {
      type: String,
    },
  },
  data() {
    return {
      OrganizationType,
      performancesList: [],
      showReportsExportModal: false,
      showCustomizeModal: false,
      displayCustomFrom: true,
      displayCustomTo: true,
      searchTerm: null,
      dateStart: null,
      dateEnd: null,
      recapitulationData: null,
      isMobileVertical: this.checkIsMobileVertical(),
    }
  },
  watch: {
    dateStart: {
      async handler() {
        await this.$apollo.queries.getEventsReport.refetch()
      },
    },
    dateEnd: {
      async handler() {
        await this.$apollo.queries.getEventsReport.refetch()
      },
    },
  },
  mounted() {
    window.addEventListener('orientationchange', this.handleOrientationChange)
  },
  beforeDestroy() {
    window.removeEventListener('orientationchange', this.handleOrientationChange)
  },
  methods: {
    handleGenerateReport(payload) {
      this.showCustomizeModal = false
      const query = {}
      for (const [key, value] of Object.entries(payload || {})) {
        if (value === 'All') {
          query[key] = undefined
        } else if (key === 'saleType') {
          const mappedSaleType = {
            'Online sales': 'ONLINE',
            'Offline sales': 'OFFLINE',
            'CoreEvent ticket offices': 'CORE_EVENT_TICKET_OFFICES',
          }
          query[key] = mappedSaleType[value]
        } else if (key === 'paymentType') {
          const mappedPaymentType = {
            Cash: 'CASH',
            'Card payment': 'CARD',
          }
          query[key] = mappedPaymentType[value]
        } else if (key === 'reportType') {
          const mappedReportType = {
            'Ticket office report': 'TICKET_OFFICE_REPORT',
            'Performance report': 'PERFORMANCE_REPORT',
          }
          query[key] = mappedReportType[value]
        } else if (key === 'periodType') {
          const mappedPeriodType = {
            'Sales Report': 'SALES_REPORT',
            'Performances Report': 'PERFORMANCES_REPORT',
          }
          query[key] = mappedPeriodType[value]
        } else if (value !== null && value !== undefined && value !== '') {
          query[key] = value
        }
      }
      const routeData = this.$router.resolve({
        name: ungroupedRouteNames.theaterAdminReport,
        params: { organizationSlug: this.organizationSlug },
        query,
      })

      window.open(routeData.href, '_blank')
    },
    getLocation(event) {
      if (event.venue) {
        return event.venue.city || ''
      } else if (event.location) {
        return event.location.city || ''
      }

      return ''
    },
    getCapacity(event) {
      if (event.venueAvailability) {
        let capacity = 0
        for (const seatInfo of Object.values(event.venueAvailability.availability)) {
          seatInfo.state !== SeatState.DISABLED && capacity++
        }

        return capacity
      } else {
        return event.audienceCapacity
      }
    },
    countPurchases(orders) {
      if (orders) {
        return orders.reduce((acc, item) => {
          return acc + item.purchases.length
        }, 0)
      }

      return 0
    },
    formatDate(createdAt) {
      return moment(createdAt).format('DD.MM.YYYY HH:mm')
    },
    sortEvents(eventList) {
      return eventList
        .filter(e => parseInt(e.id) !== 1078)
        .sort((event1, event2) => new Date(event2.from) - new Date(event1.from))
    },
    modifyPerformanceData(performances, getEventsReport) {
      this.recapitulationData = {
        eventAmount: 0,
        capacity: 0,
        totalTickets: 0,
        available: 0,
        reserved: 0,
        sold: 0,
        gratis: 0,
        promoCode: 0,
        sumAveragePrice: 0,
        numberOfTransactions: 0,
        sumTotalOccupancy: 0,
        totalRevenue: 0,
      }
      for (const event of performances) {
        const report = getEventsReport.reports[event.id].salesData
        if (!report) {
          continue
        }
        let totalSoldOnlineAmount = 0
        let totalSoldOnlineCount = 0
        let totalSoldOfflineCount = 0
        let totalSoldOfflineAmount = 0
        let totalWithCoupon = 0
        let totalWithPromoCode = 0
        let totalOrderCount = 0
        for (const r of report) {
          if (parseInt(r.offline) > 0) {
            totalSoldOfflineCount += parseInt(r.offline)
            totalSoldOfflineAmount += parseFloat(r.total)
          } else {
            totalSoldOnlineCount += parseInt(r.online)
            totalSoldOnlineAmount += parseFloat(r.total)
          }
          if (r.prepaidCoupon) {
            totalWithCoupon += parseInt(r.online) + parseInt(r.offline)
          }
          if (r.promocode) {
            totalWithPromoCode += parseInt(r.online) + parseInt(r.offline)
          }
          totalOrderCount += parseInt(r.orderCount)
        }
        const totalSoldCount = totalSoldOfflineCount + totalSoldOnlineCount
        const totalSoldAmount = totalSoldOfflineAmount + totalSoldOnlineAmount

        event.report = {
          totalSoldCount,
          totalSoldAmount,
          totalSoldOfflineCount,
          totalSoldOfflineAmount,
          totalSoldOnlineCount,
          totalSoldOnlineAmount,
          totalWithCoupon,
          totalWithPromoCode,
          totalOrderCount,
          totalOccupancy:
            ((
              (100 *
                (this.countPurchases(getEventsReport.reports[event.id].reservations) +
                  totalSoldCount +
                  this.countPurchases(getEventsReport.reports[event.id].gratisOrders))) /
              event.audienceCapacity
            ).toFixed(2) || '/') + ' %',
          averagePrice: round(totalSoldAmount / totalSoldCount, 2) || '/',
          reservations: getEventsReport.reports[event.id].reservations,
          gratisOrders: getEventsReport.reports[event.id].gratisOrders,
        }
        const eventReservationCount = this.countPurchases(event.report.reservations)
        const eventGratisCount = this.countPurchases(event.report.gratisOrders)

        ++this.recapitulationData.eventAmount
        this.recapitulationData.capacity +=
          (event.venueAvailability && this.getCapacity(event)) || event.audienceCapacity
        this.recapitulationData.totalTickets += event.audienceCapacity
        this.recapitulationData.available +=
          event.audienceCapacity -
          eventReservationCount -
          event.report.totalSoldCount -
          eventGratisCount
        this.recapitulationData.reserved += eventReservationCount
        this.recapitulationData.sold += event.report.totalSoldCount
        this.recapitulationData.gratis += eventGratisCount
        this.recapitulationData.prepaidCoupon += event.report.totalWithCoupon
        this.recapitulationData.promoCode += event.report.totalWithPromoCode
        this.recapitulationData.sumAveragePrice += round(totalSoldAmount / totalSoldCount, 2) || 0
        this.recapitulationData.numberOfTransactions += event.report.totalOrderCount
        this.recapitulationData.sumTotalOccupancy +=
          (100 * (eventReservationCount + event.report.totalSoldCount + eventGratisCount)) /
            event.audienceCapacity || 0
        this.recapitulationData.totalRevenue +=
          event.report.totalSoldOnlineAmount + event.report.totalSoldOfflineAmount
      }
    },
    removeSecondValuesInCells() {
      const columnsIndexesWithDoubleValues = [6, 7, 8, 9]
      const table = document.getElementById('data-table')

      const clonedTable = table.cloneNode(true)

      if (!clonedTable || !clonedTable.childNodes[1] || !clonedTable.childNodes[1].childNodes) {
        return table
      }

      for (const row of clonedTable.childNodes[1].childNodes) {
        for (const [index, column] of Array.from(row.childNodes).entries()) {
          if (!columnsIndexesWithDoubleValues.includes(index)) {
            continue
          }

          column.textContent = column.textContent.trim().split(' ')[0]
        }
      }

      return clonedTable
    },
    handleExcelExport() {
      const tableToExport = this.removeSecondValuesInCells()
      const ws = XLSX.utils.table_to_sheet(tableToExport, { raw: true })
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, 'exported_reports.xlsx')
    },
    handleCsvExport() {
      const tableToExport = this.removeSecondValuesInCells()
      const ws = XLSX.utils.table_to_sheet(tableToExport)
      const csv = XLSX.utils.sheet_to_csv(ws)
      const csvData = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      if (link.download !== undefined) {
        const url = URL.createObjectURL(csvData)
        link.setAttribute('href', url)
        link.setAttribute('download', 'exported_reports.csv')
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    },
    async handleSearchChange(input) {
      this.searchTerm = input
      await this.$apollo.queries.getEventsReport.refetch()
    },
    handleOrientationChange() {
      this.isMobileVertical = this.checkIsMobileVertical()
    },
    checkIsMobileVertical() {
      return (
        /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) &&
        screen.orientation.type.includes('portrait')
      )
    },
  },
  apollo: {
    getEventsReport: {
      query: GET_THEATER_SUMMARY_REPORTS,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          searchTerm: this.searchTerm,
          from: this.dateStart,
          to: this.dateEnd,
        }
      },
      result({ data: { getEventsReport }, loading }) {
        if (loading) {
          return
        }
        this.performancesList = this.sortEvents(getEventsReport.events)
        this.modifyPerformanceData(this.performancesList, getEventsReport)
      },
    },
  },
}
</script>

<style scoped>
.subheader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.table-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
  @media screen and (max-width: 537px) {
    justify-content: flex-start;
  }
}

.filter-dates-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  flex: 1 0 0;
  min-width: 18rem;
}

tr {
  border: 1px solid var(--Grey-30, #d9d9d9);
  background: white;
}
.tr-right-border {
  border-right: 1px solid var(--Grey-30, #d9d9d9);
}
td {
  text-align: center;
  margin: 0;
  padding: 0.75rem;
  width: 10rem;
}

td:first-child {
  text-align: center;
}

th {
  margin: 0;
  padding: 0.75rem;
  width: 8rem;
}

tbody {
  border: 1px solid var(--grey-40, #f5f5f5);
}

th:first-child {
  border-radius: 0.5rem 0 0 0;
}

th:last-child {
  border-radius: 0 0.5rem 0 0;
}

table {
  table-layout: fixed;
  width: 100%;
  overflow-x: scroll;
  border-collapse: collapse;
}

.fixed-td {
  position: sticky;
  width: 200px;
  z-index: 2;
  left: 0;
  background-color: #fff;
  @media screen and (max-width: 600px) {
    width: 150px;
  }
}
.fixed-hd {
  position: sticky;
  top: 0;
  z-index: 1;
}

.left-top-td {
  z-index: 3;
}

.left-top-td-second {
  z-index: 3;
  left: 200px;
  @media screen and (max-width: 600px) {
    left: 150px;
  }
}

.fixed-td-second {
  left: 200px;
  @media screen and (max-width: 600px) {
    left: 150px;
  }
}

tfoot {
  height: 2rem;
  border: 1px solid var(--grey-40, #f5f5f5);
}

.report-container {
  padding: 0 !important;
  margin: 0 1.5rem !important;
  display: flex;
  flex-direction: column;
  font-size: 1rem !important;
  /* max-width: 43.75rem; */
}

.tables-container {
  overflow: auto;
  position: sticky;
  background-color: #fff;
  top: 80px;
  @media screen and (max-width: 425px) {
    top: 70px;
  }
}
.table-container {
  overflow: auto;
  max-height: 80vh;
}

.table-container-recapitulation {
  overflow: auto;
  margin-top: 2rem;
}

.subs-title {
  color: #333;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.045rem;
}

.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.right-controls {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.export-button {
  display: flex;
  height: 3rem;
  padding: 0.75rem 1.25rem 0.75rem 1.5rem;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border-radius: 1.5rem;
  background: #0277bd;
  margin-bottom: 22px;
}

.customize-button {
  display: flex;
  height: 3rem;
  padding: 0.75rem 1.25rem 0.75rem 1.5rem;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0);
  border: 1px solid #0277bd;
  margin-bottom: 22px;
}

.export-button:hover,
.mobile-export-button:hover {
  cursor: pointer;
}

.customize-button:hover {
  cursor: pointer;
}

.export-text {
  color: #fff;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: Raleway;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.01688rem;
  text-transform: uppercase;
}

.customize-text {
  color: #0277bd;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: Raleway;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.01688rem;
  text-transform: uppercase;
}

.header-row th {
  overflow: auto;
  position: sticky;
  background-color: #fff;
  top: 0;
}

@media screen and (max-width: 400px) {
  .mobile-table-header,
  .mobile-tables-container {
    display: none;
  }
}

.rotate-phone-message {
  display: block;
  color: #000;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
  letter-spacing: -0.045rem;
  width: 60%;
  margin: 2rem auto 0 auto;
}

.rotate-phone-img {
  display: block;
  width: 6.1875rem;
  height: 6.1875rem;
  margin: 5rem auto 0 auto;
}

.mobile-export-container {
  display: flex;
  padding: 1rem;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  border-radius: 0.5rem;
  border: 1px solid #d9d9d9;
  margin-top: 5rem;
}

.mobile-export-description {
  display: block;
  width: 100%;
  color: #666;
  text-align: center;
  font-family: Source Sans Pro;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
}

.mobile-export-button {
  display: flex;
  height: 3rem;
  padding: 0.75rem 1.25rem 0.75rem 1.5rem;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border-radius: 1.5rem;
  background: rgba(255, 255, 255, 0);
}

.mobile-export-text {
  color: #0277bd;
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: Raleway;
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: -0.01688rem;
  text-transform: uppercase;
}
</style>
